/** Portfolio-level mocks: reports, integrations and admin. */
import type { Integration, PolicyLimit, PortfolioStat, ReportSummary, TeamMember } from "@/lib/types";

export const reports: ReportSummary[] = [
  {
    id: "rep-pipeline",
    name: "Origination pipeline",
    description: "Every open case by stage, age and requested amount, with the bottleneck stage highlighted.",
    cadence: "Live",
    lastRun: "2026-08-24T14:35:00Z",
  },
  {
    id: "rep-decisions",
    name: "Decision log",
    description: "Approvals, declines and conditions issued, with the analyst and the evidence each decision rested on.",
    cadence: "Daily",
    lastRun: "2026-08-24T06:00:00Z",
  },
  {
    id: "rep-exceptions",
    name: "Exception register",
    description: "Unverified and flagged extracted values across all open cases, ranked by the amount at risk.",
    cadence: "Live",
    lastRun: "2026-08-24T14:35:00Z",
  },
  {
    id: "rep-concentration",
    name: "Portfolio concentration",
    description: "Exposure by industry, geography and single obligor against policy limits.",
    cadence: "Weekly",
    lastRun: "2026-08-21T07:00:00Z",
  },
  {
    id: "rep-model",
    name: "Model performance",
    description: "Predicted versus realised grade migration for Credit Model v4 since deployment.",
    cadence: "Monthly",
    lastRun: "2026-08-01T07:00:00Z",
  },
];

export const portfolioStats: PortfolioStat[] = [
  { id: "st-open", label: "Open cases", value: "3", delta: "+1 this week" },
  { id: "st-requested", label: "Requested volume", value: "$1.87M" },
  { id: "st-cycle", label: "Median cycle time", value: "9 days", delta: "−2 days" },
  { id: "st-exceptions", label: "Open exceptions", value: "5", delta: "+2" },
];

export const integrations: Integration[] = [
  {
    id: "int-plaid",
    name: "Plaid",
    category: "Banking",
    status: "connected",
    detail: "Transaction and balance data for connected borrower accounts.",
    lastSync: "2026-08-24T13:52:00Z",
  },
  {
    id: "int-qbo",
    name: "QuickBooks Online",
    category: "Accounting",
    status: "connected",
    detail: "General ledger, AR aging and customer revenue split.",
    lastSync: "2026-08-24T06:15:00Z",
  },
  {
    id: "int-experian",
    name: "Experian Business",
    category: "Bureau",
    status: "connected",
    detail: "Intelliscore Plus, public records and trade payment history.",
    lastSync: "2026-08-21T11:00:00Z",
  },
  {
    id: "int-drive",
    name: "Google Drive",
    category: "Documents",
    status: "error",
    detail: "Token expired 22 Aug. Borrower-shared folders are not syncing.",
    lastSync: "2026-08-22T04:00:00Z",
  },
  {
    id: "int-xero",
    name: "Xero",
    category: "Accounting",
    status: "available",
    detail: "Alternative ledger source for borrowers not on QuickBooks.",
  },
  {
    id: "int-salesforce",
    name: "Salesforce",
    category: "CRM",
    status: "available",
    detail: "Sync opportunities into the inbox as originating cases.",
  },
];

export const team: TeamMember[] = [
  {
    id: "u-tom",
    name: "Tom Giladi",
    email: "tom@example.com",
    role: "Underwriter",
    lastActive: "2026-08-24T14:35:00Z",
  },
  {
    id: "u-dana",
    name: "Dana Okafor",
    email: "dana@example.com",
    role: "Credit Officer",
    lastActive: "2026-08-24T11:02:00Z",
  },
  {
    id: "u-ilan",
    name: "Ilan Weiss",
    email: "ilan@example.com",
    role: "Analyst",
    lastActive: "2026-08-23T17:44:00Z",
  },
  {
    id: "u-mira",
    name: "Mira Kaplan",
    email: "mira@example.com",
    role: "Admin",
    lastActive: "2026-08-20T09:10:00Z",
  },
];

export const policyLimits: PolicyLimit[] = [
  { id: "pol-size", label: "Maximum facility", value: "$2,500,000", note: "Above this requires committee approval." },
  { id: "pol-leverage", label: "Leverage cap", value: "3.50x", note: "Debt / adjusted EBITDA at close." },
  { id: "pol-dscr", label: "DSCR floor", value: "1.20x", note: "Base case. Downside may not fall below 0.90x." },
  { id: "pol-concentration", label: "Single-obligor limit", value: "8% of portfolio", note: "Measured on committed amount." },
  { id: "pol-deposit", label: "Deposit variance threshold", value: "15%", note: "Above this the cash-flow model flags." },
];
